import * as angular from 'angular';
import * as _ from 'lodash';

import { ITagDefinitionService, IVariableService, IGroupService, IConnectionPointService, ITaskService, VariableTypeDto } from '..';
import { TagViewModel, IdentifierType, Identifier, TagDefinition, VariableType, ConnectionPoint, ConnectionPointDTO, AcquistionTaskConfiguration, GroupDto, IDateRange } from '../../models';
import { guidEmpty } from '../../common';
import { TagDefinitionDTO } from '../../models';

export interface IGroupTagsService {
    getViewModels(groupGuid: string): angular.IPromise<TagViewModel[]>;
    addTagsToGroup(groupGuid: string, tagGuids: string[]): angular.IPromise<GroupDto>;
    acquire(guids: string[], beginDate: string, endDate: string, force?: boolean): angular.IPromise<IDateRange[]>;
}

export class GroupTagsService implements IGroupTagsService {
    static serviceId = 'groupTagsService';
    static $inject = ['$q', 'tagDefinitionService', 'variableService', 'groupService', 'connectionPointService', 'taskService'];

    constructor(private _$q: angular.IQService,
        private _tagDefinitionService: ITagDefinitionService,
        private _variableService: IVariableService,
        private _groupService: IGroupService,
        private _connectionPointService: IConnectionPointService,
        private _taskService: ITaskService) {
    }

    getViewModels(groupGuid: string): angular.IPromise<TagViewModel[]> {
        const defered = this._$q.defer<TagViewModel[]>();
        if (groupGuid !== guidEmpty) {
            this.getGroupViewModels(groupGuid, defered);
        } else {
            this._tagDefinitionService.getUnused()
                .then(data => {
                    return this.setVariables(data, []);
                }).then(result => {
                    defered.resolve(result);
                }).catch(() => {
                    defered.reject();
                });
        }
        return defered.promise;
    }

    private getGroupViewModels(guid: string, defered: angular.IDeferred<any>) {
        let tagIdentifiers: Identifier[];
        this._groupService
            .getByGuid(guid)
            .then(data => {
                tagIdentifiers = data.group.identifierCollection
                    .filter(i => i.type === IdentifierType.Tag);
                const guids = _.uniq(tagIdentifiers)
                    .map(i => i.tagDefinitionGuid);
                if (guids.length > 0) {
                    return this._tagDefinitionService.getByGuids(guids);
                } else {
                    defered.resolve([]);
                }
            })
            .then(data => {
                return this.setVariables(data, tagIdentifiers);
            })
            .then((result: TagViewModel[]) => {
                defered.resolve(result);
            })
            .catch(() => {
                defered.reject();
            });
    }

    private setVariables(tagsDto: TagDefinitionDTO[], tagIdentifiers: Identifier[]):
        angular.IPromise<TagViewModel[]> {
        const defered = this._$q.defer<TagViewModel[]>();
        if (tagsDto.length > 0) {
            const tags = tagsDto.map(d => d.tagDefinition);
            const variableTypeGuids = _.uniq(tags.map(t => t.variableTypeGuid));
            const connectionPointGuids = _.uniq(tags.map(t => t.acquisitionPointGuid));
            const promises = [
                this._variableService.getList(variableTypeGuids),
                this._connectionPointService.getList(connectionPointGuids)
            ];
            this._$q.all(promises)
                .then(responses => {
                    const variables = (responses[0] as VariableTypeDto[]).map(d => d.variable);
                    const connectionPoints = (responses[1] as ConnectionPointDTO[]).map(c => c.connectionPoint);
                    defered.resolve(this.createViewModels(tagIdentifiers, tags, variables, connectionPoints));
                })
                .catch(() => {
                    defered.reject();
                });
        } else {
            defered.resolve([]);
        }
        return defered.promise;
    }

    private createViewModels(tagIdentifiers: Identifier[],
        tags: TagDefinition[],
        types: VariableType[],
        connectionPoints: ConnectionPoint[]): TagViewModel[] {
        const result: TagViewModel[] = tags.map(t => {
            const variable = _.find(types, d => d.guid === t.variableTypeGuid);
            const unit: any = variable ? _.find(variable.unitCollection, (u: any) => u.guid === t.unitGuid) : null;
            const connectionPoint = _.find(connectionPoints, c => c.guid === t.acquisitionPointGuid);
            const identifierList = tagIdentifiers.filter(i => i.tagDefinitionGuid === t.guid);
            const identifierNames = identifierList.map(i => i.name).join(',');
            return {
                name: t.name,
                guid: t.guid,
                type: variable ? variable.name : '',
                unit: unit ? unit.name : '',
                identifierName: identifierNames,
                source: connectionPoint ? connectionPoint.name : '',
                tagDefinition: t,
                uniqueIdentifier: (identifierList.length > 1 || identifierList.length === 0) ? null : identifierList[0]
            };
        });
        return result;
    }

    addTagsToGroup(groupGuid: string, tagGuids: string[]): angular.IPromise<any> {
        const postObject = {
            guid: groupGuid,
            tags: tagGuids
        };
        return this._groupService.createGroupFromTags(postObject);
    }

    acquire(guids: string[], beginDate: string, endDate: string, force: boolean = false): angular.IPromise<IDateRange[]> {
        return this._taskService.createAcquisitionTask({
            beginDateTime: beginDate,
            endDateTime: endDate,
            tagsList: guids
        } as AcquistionTaskConfiguration, force);
    }
}